/**
 * كشف البيئة الحاليّة (dev/staging/prod) اعتماداً على hostname.
 *
 * - dev: معاينة Lovable (id-preview--…)، localhost، dev subdomain.
 * - staging: `staging.*` ، `*-staging.*` ، `*.staging.*`.
 * - prod: `lecmo.com`، `www.lecmo.com`، وأيّ نشر رسميّ.
 *
 * الغرض: نفس قاعدة البيانات مشتركة بين البيئات، فيسمح هذا الملفّ لتوغلات
 * الميزات بأن تكون منفصلة لكلّ بيئة بحيث لا يؤثّر تبديل الأدمن في المعاينة
 * على النطاق الإنتاجيّ.
 */

export type AppEnv = "dev" | "staging" | "prod";

export const APP_ENVS: AppEnv[] = ["dev", "staging", "prod"];

export function envFromHost(host: string | null | undefined): AppEnv {
  const h = (host || "").toLowerCase().trim();
  if (!h) return "prod";
  if (
    h === "localhost" ||
    h.startsWith("localhost:") ||
    h.startsWith("127.") ||
    h.endsWith(".local") ||
    h.includes("id-preview--") ||
    h.includes("--dev.lovable.app") ||
    h.endsWith("-dev.lovable.app") ||
    h.includes(".lovableproject.com")
  ) {
    return "dev";
  }
  if (
    h.startsWith("staging.") ||
    h.includes("-staging.") ||
    h.includes(".staging.")
  ) {
    return "staging";
  }
  if (h === "lecmo.com" || h === "www.lecmo.com") {
    return "prod";
  }

  return "prod";
}

/** تُستخدم على العميل فقط. عند SSR تُرجع "prod". */
export function envFromWindow(): AppEnv {
  if (typeof window === "undefined" || !window.location) return "prod";
  return envFromHost(window.location.hostname);
}

export const ENV_META: Record<
  AppEnv,
  { ar: string; en: string; badgeClass: string; dotClass: string }
> = {
  dev: {
    ar: "تطوير",
    en: "Dev",
    badgeClass:
      "bg-blue-100 text-blue-900 dark:bg-blue-950/60 dark:text-blue-200 border-blue-200 dark:border-blue-900",
    dotClass: "bg-blue-500",
  },
  staging: {
    ar: "تجريبي",
    en: "Staging",
    badgeClass:
      "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 border-amber-200 dark:border-amber-900",
    dotClass: "bg-amber-500",
  },
  prod: {
    ar: "إنتاج",
    en: "Prod",
    badgeClass:
      "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900",
    dotClass: "bg-emerald-500",
  },
};

/**
 * يستخرج قيمة env-scoped من قيمة app_settings.
 * أشكال مدعومة:
 *  - boolean/number/string خام → نفس القيمة لكل البيئات (نمط قديم).
 *  - { dev?, staging?, prod? } → قيمة البيئة المطلوبة (مع fallback prod→staging→dev).
 *  - { value: X } نمط قديم → X.
 */
export function pickEnvValue(raw: unknown, env: AppEnv): unknown {
  if (raw === null || raw === undefined) return raw;
  if (typeof raw !== "object" || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  const hasEnvKeys = "dev" in obj || "staging" in obj || "prod" in obj;
  if (hasEnvKeys) {
    if (env in obj && obj[env] !== undefined && obj[env] !== null) return obj[env];
    const order: AppEnv[] = ["prod", "staging", "dev"];
    for (const e of order) {
      if (e in obj && obj[e] !== undefined && obj[e] !== null) return obj[e];
    }
    return undefined;
  }
  if ("value" in obj) return obj.value;
  return raw;
}

/** استخراج الأشكال الثلاث معاً — تُستخدم في لوحة الأدمن. */
export function normalizeEnvBool(
  raw: unknown,
  defaultOn: boolean,
): Record<AppEnv, boolean> {
  const asBool = (x: unknown): boolean | null => {
    if (typeof x === "boolean") return x;
    if (x === "true" || x === 1) return true;
    if (x === "false" || x === 0) return false;
    return null;
  };
  // خام: bool/number/string يشمل كل البيئات
  if (raw === null || raw === undefined) {
    return { dev: defaultOn, staging: defaultOn, prod: defaultOn };
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    const b = asBool(raw);
    const v = b === null ? defaultOn : b;
    return { dev: v, staging: v, prod: v };
  }
  const obj = raw as Record<string, unknown>;
  const hasEnvKeys = "dev" in obj || "staging" in obj || "prod" in obj;
  if (!hasEnvKeys) {
    // نمط {value: bool} أو غير معروف → نطبّقه على الكل
    const b = asBool("value" in obj ? obj.value : null);
    const v = b === null ? defaultOn : b;
    return { dev: v, staging: v, prod: v };
  }
  const pick = (e: AppEnv): boolean => {
    const b = asBool(obj[e]);
    if (b !== null) return b;
    // fallback: أيّ بيئة أخرى محدَّدة، وإلّا defaultOn
    for (const alt of ["prod", "staging", "dev"] as AppEnv[]) {
      if (alt === e) continue;
      const bAlt = asBool(obj[alt]);
      if (bAlt !== null) return bAlt;
    }
    return defaultOn;
  };
  return { dev: pick("dev"), staging: pick("staging"), prod: pick("prod") };
}
